import { Box, Container, Typography, alpha } from '@mui/material';
import { IconButton } from '@mui/material';
import { ArrowBack, Security } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout';
import { brandColors } from '@/theme/colors';

export const PrivacyPage = () => {
  const navigate = useNavigate();
  const effectiveDate = 'January 1, 2025';

  return (
    <AppLayout title="Privacy Policy">
      {/* Hero Header */}
      <Box
        sx={{
          background: brandColors.gradient,
          py: { xs: 4, md: 6 },
          mb: 4,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.15) 0%, transparent 40%)',
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: 'white' }}>
              <ArrowBack />
            </IconButton>
            <Security sx={{ fontSize: 40, color: 'white' }} />
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 700,
                color: 'white',
                textShadow: '0 2px 10px rgba(0,0,0,0.2)',
              }}
            >
              Privacy Policy
            </Typography>
          </Box>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.9)',
              ml: 7,
              fontWeight: 400,
            }}
          >
            Effective Date: {effectiveDate}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ pb: 8 }}>
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: 3,
            p: { xs: 3, md: 5 },
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            '& h2': {
              fontSize: '1.5rem',
              fontWeight: 600,
              mt: 4,
              mb: 2,
              color: brandColors.charcoal,
              borderBottom: `2px solid ${alpha(brandColors.purple, 0.2)}`,
              pb: 1,
            },
            '& h3': {
              fontSize: '1.2rem',
              fontWeight: 600,
              mt: 3,
              mb: 1.5,
              color: brandColors.purpleDeep,
            },
            '& p': {
              mb: 2,
              lineHeight: 1.8,
              color: '#555',
            },
            '& ul': {
              mb: 2,
              pl: 3,
            },
            '& li': {
              mb: 1,
              lineHeight: 1.7,
              color: '#555',
            },
          }}
        >
          <Typography variant="body1" sx={{ mb: 3, fontStyle: 'italic', color: '#666' }}>
            This Privacy Policy explains how Advancer Limited collects, uses, and protects your information
            when you use ThinkSpace.
          </Typography>

          <Typography variant="h2">1. Data Controller</Typography>
          <Typography variant="body1">
            Advancer Limited, a company registered in New Zealand, is the data controller for personal information
            collected through ThinkSpace. Our contact details are:
          </Typography>
          <Box sx={{
            bgcolor: alpha(brandColors.purple, 0.05),
            p: 3,
            borderRadius: 2,
            borderLeft: `4px solid ${brandColors.purple}`,
            my: 2,
          }}>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Advancer Limited</strong> (NZBN: 9429041814352)
            </Typography>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              2/14 Parkway Drive, Mairangi Bay, Auckland 0632, New Zealand
            </Typography>
            <Typography variant="body1">
              Email: info@mythinkspace.app
            </Typography>
          </Box>

          <Typography variant="h2">2. Information We Collect</Typography>
          <Typography variant="h3">2.1 Account Information</Typography>
          <Typography variant="body1">
            When you sign up using Google authentication, we receive:
          </Typography>
          <ul>
            <li>Your name (as provided by Google)</li>
            <li>Your email address</li>
            <li>Your Google profile photo URL</li>
            <li>Your Google user ID (for authentication purposes)</li>
          </ul>

          <Typography variant="h3">2.2 Content You Create</Typography>
          <Typography variant="body1">
            We store content you create within ThinkSpace, including:
          </Typography>
          <ul>
            <li>ThinkSpaces (workspaces) and their contents</li>
            <li>Sticky notes, text, shapes, and connectors</li>
            <li>Images you upload</li>
            <li>Sharing permissions and collaborator lists</li>
          </ul>

          <Typography variant="h3">2.3 Usage Information</Typography>
          <Typography variant="body1">
            We may collect information about how you use the Service, including:
          </Typography>
          <ul>
            <li>Login timestamps and session information</li>
            <li>Feature usage patterns</li>
            <li>Error logs and diagnostic information</li>
          </ul>

          <Typography variant="h2">3. Third-Party Data Processors</Typography>
          <Typography variant="body1">
            ThinkSpace is built on Google Firebase platform. By using our Service, your data is processed by
            Google as a sub-processor under our instruction. Google processes data in accordance with their
            Data Processing Terms available at{' '}
            <a href="https://cloud.google.com/terms/data-processing-terms" target="_blank" rel="noopener noreferrer">
              https://cloud.google.com/terms/data-processing-terms
            </a>.
          </Typography>

          <Typography variant="h3">3.1 Firebase Services We Use</Typography>
          <ul>
            <li><strong>Firebase Authentication:</strong> Manages user sign-in via Google OAuth</li>
            <li><strong>Cloud Firestore:</strong> Stores your ThinkSpaces and collaboration data</li>
            <li><strong>Cloud Storage:</strong> Stores images you upload to ThinkSpaces</li>
            <li><strong>Firebase Hosting:</strong> Serves the ThinkSpace web application</li>
          </ul>

          <Typography variant="h3">3.2 Data Location</Typography>
          <Typography variant="body1">
            Data processed by Firebase may be stored in data centers located in various regions.
            Google maintains data processing agreements and implements appropriate safeguards for
            international data transfers as required by applicable law.
          </Typography>

          <Typography variant="h2">4. How We Use Your Information</Typography>
          <Typography variant="body1">
            We use your information to:
          </Typography>
          <ul>
            <li>Provide, maintain, and improve the Service</li>
            <li>Authenticate your identity and manage your account</li>
            <li>Enable collaboration features and sharing</li>
            <li>Send service-related communications (e.g., welcome emails, support responses)</li>
            <li>Diagnose technical issues and improve performance</li>
            <li>Comply with legal obligations</li>
          </ul>

          <Typography variant="h2">5. Legal Basis for Processing</Typography>
          <Typography variant="body1">
            We process your personal information based on:
          </Typography>
          <ul>
            <li><strong>Contract:</strong> Processing necessary to provide the Service you have requested</li>
            <li><strong>Legitimate Interests:</strong> Processing for our legitimate business interests,
              such as improving the Service and ensuring security</li>
            <li><strong>Legal Obligations:</strong> Processing required to comply with applicable laws</li>
          </ul>

          <Typography variant="h2">6. Data Sharing</Typography>
          <Typography variant="body1">
            We do not sell your personal information. We share your information only in the following circumstances:
          </Typography>
          <ul>
            <li><strong>With your consent:</strong> When you share ThinkSpaces with other users</li>
            <li><strong>With service providers:</strong> Google (Firebase) as described above</li>
            <li><strong>For legal reasons:</strong> When required by law, court order, or governmental authority</li>
            <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
          </ul>

          <Typography variant="h2">7. Data Retention</Typography>
          <Typography variant="body1">
            We retain your personal information for as long as your account is active or as needed to provide
            the Service. When you delete your account, we will delete or anonymize your personal information
            within 30 days, except where retention is required by law.
          </Typography>
          <Typography variant="body1">
            Content you create (ThinkSpaces) is retained until you delete it or close your account.
            Shared content may remain visible to collaborators until deleted.
          </Typography>

          <Typography variant="h2">8. Your Rights</Typography>
          <Typography variant="body1">
            Subject to applicable law, you have the right to:
          </Typography>
          <ul>
            <li><strong>Access:</strong> Request a copy of your personal information</li>
            <li><strong>Correction:</strong> Request correction of inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information</li>
            <li><strong>Data Portability:</strong> Export your ThinkSpace data in a portable format</li>
            <li><strong>Objection:</strong> Object to certain processing of your information</li>
          </ul>
          <Typography variant="body1">
            To exercise these rights, please contact us at info@mythinkspace.app.
          </Typography>

          <Typography variant="h2">9. Data Security</Typography>
          <Typography variant="body1">
            We implement appropriate technical and organizational measures to protect your information, including:
          </Typography>
          <ul>
            <li>Encryption of data in transit (HTTPS/TLS)</li>
            <li>Encryption of data at rest in Firebase</li>
            <li>Access controls and authentication requirements</li>
            <li>Regular security reviews of our practices</li>
          </ul>
          <Typography variant="body1">
            However, no method of transmission or storage is completely secure. We cannot guarantee absolute security.
          </Typography>

          <Typography variant="h2">10. Children's Privacy</Typography>
          <Typography variant="body1">
            ThinkSpace is not intended for children under 16 years of age. We do not knowingly collect
            personal information from children under 16. If you believe we have collected information
            from a child under 16, please contact us immediately.
          </Typography>

          <Typography variant="h2">11. Cookies and Tracking</Typography>
          <Typography variant="body1">
            ThinkSpace uses essential cookies and local storage for:
          </Typography>
          <ul>
            <li>Maintaining your login session</li>
            <li>Storing user preferences</li>
            <li>Enabling core functionality</li>
          </ul>
          <Typography variant="body1">
            We do not use third-party tracking cookies for advertising purposes.
          </Typography>

          <Typography variant="h2">12. International Data Transfers</Typography>
          <Typography variant="body1">
            Your information may be transferred to and processed in countries other than New Zealand.
            When we transfer data internationally, we ensure appropriate safeguards are in place
            as required by the Privacy Act 2020 (New Zealand) and other applicable data protection laws.
          </Typography>

          <Typography variant="h2">13. Changes to This Policy</Typography>
          <Typography variant="body1">
            We may update this Privacy Policy from time to time. We will notify you of material changes
            by posting the updated policy on the Service and updating the "Effective Date" above.
            Your continued use of the Service after changes are posted constitutes acceptance of the updated policy.
          </Typography>

          <Typography variant="h2">14. Contact Us</Typography>
          <Typography variant="body1">
            If you have questions about this Privacy Policy or our data practices, please contact us at:
          </Typography>
          <Box sx={{
            bgcolor: alpha(brandColors.purple, 0.05),
            p: 3,
            borderRadius: 2,
            borderLeft: `4px solid ${brandColors.purple}`,
            mt: 2,
          }}>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Advancer Limited</strong> (NZBN: 9429041814352)
            </Typography>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              2/14 Parkway Drive, Mairangi Bay
            </Typography>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              Auckland 0632, New Zealand
            </Typography>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              Email: info@mythinkspace.app
            </Typography>
            <Typography variant="body1">
              Phone: +64 9 489 3830
            </Typography>
          </Box>

          <Typography variant="h2">15. Governing Law</Typography>
          <Typography variant="body1">
            This Privacy Policy is governed by the laws of New Zealand, including the Privacy Act 2020.
          </Typography>
        </Box>
      </Container>
    </AppLayout>
  );
};
